import type { ResultOf } from '@graphql-typed-document-node/core'
import { atom, useAtomValue, useSetAtom } from 'jotai'
import { atomWithQuery } from 'jotai-tanstack-query'
import { useCallback } from 'react'
import type { ReserveStateDatetime } from '~/features/reserve/state'
import { getEndOfMonthAfterDays, getMonth } from '~/lib/date/date'
import { getFragmentData } from '~/lib/graphql'
import type {
  PlanInventoryCalendarInput,
  PlanInventoryCalendarQueryVariables,
  PlanReservableVisitorsCountsQueryVariables,
} from '~/lib/graphql/graphql'
import type { DateText, PlanId, RestaurantId } from '~/lib/graphql/scalars'
import { createScope } from '~/lib/jotai'
import { elementToValue } from '~/lib/selectBox'
import type { AdditionalCriteria, CalendarContext } from '~/navigation'
import { createQueryAtom, createQueryOptions } from '~/stores/jotaiQueryHelper'
import { now$, today$ } from '~/stores/now'
import {
  InventoryCalendarItem,
  PlanInventoryCalendar,
  PlanReservableVisitorsCounts,
} from './graphql'
import input from './mock/input.json'
import output from './mock/output.json'

const DEBUG_PREFIX = 'VisitCondition'

export const scope = createScope(DEBUG_PREFIX)

const state$ = scope.atom<ReserveStateDatetime | undefined>(undefined, 'context$')

export function useSetState() {
  return useSetAtom(state$)
}

const reservableVisitorsCountsVariables$ = atom<PlanReservableVisitorsCountsQueryVariables>(
  (get) => {
    const state = get(state$)
    if (state === undefined) {
      return {
        restaurantId: '' as RestaurantId,
        planId: '' as PlanId,
      }
    }
    return {
      restaurantId: state.restaurantId,
      planId: state.planId,
    }
  }
)
reservableVisitorsCountsVariables$.debugLabel = `${DEBUG_PREFIX}-reservableVisitorsCountsVariables$`

const reservableVisitorsCountsQuery$ = createQueryAtom<typeof PlanReservableVisitorsCounts>()
reservableVisitorsCountsQuery$.debugLabel = `${DEBUG_PREFIX}-reservableVisitorsCountsQuery$`

const reservableVisitorsCounts$ = atomWithQuery((get) =>
  createQueryOptions(
    PlanReservableVisitorsCounts,
    get(reservableVisitorsCountsVariables$),
    get(reservableVisitorsCountsQuery$).fn,
    {
      enabled: get(reservableVisitorsCountsVariables$).restaurantId !== '',
    }
  )
)
reservableVisitorsCounts$.debugLabel = `${DEBUG_PREFIX}-reservableVisitorsCounts$`

export function useReservableVisitorsCountsQuery() {
  return useAtomValue(reservableVisitorsCounts$)
}

const plan$ = atom((get) => {
  const data = get(reservableVisitorsCounts$).data
  if (data === undefined) {
    return undefined
  }
  return data.restaurant?.plan ?? undefined
})
plan$.debugLabel = `${DEBUG_PREFIX}-plan$`

export function usePlan() {
  return useAtomValue(plan$)
}

const reservableVisitorsCountsResult$ = atom((get) => {
  const data = get(reservableVisitorsCounts$).data
  if (data === undefined) {
    return []
  }

  const visitorsCounts = data.restaurant?.reservableVisitorsCounts ?? []
  if (visitorsCounts.length > 0) {
    return visitorsCounts
  }

  // 在庫を考慮した予約可能人数が取得できない場合は、プランに設定されている利用可能人数を用いる
  const plan = data.restaurant?.plan
  if (!plan) {
    return []
  }
  return reservableVisitorsCountsFromCapacityRange(plan.capacityRange)
})
reservableVisitorsCountsResult$.debugLabel = `${DEBUG_PREFIX}-reservableVisitorsCountsResult$`

export function useReservableVisitorsCounts() {
  return useAtomValue(reservableVisitorsCountsResult$)
}

function reservableVisitorsCountsFromCapacityRange(capacityRange: {
  lower: number
  upper: number
}): number[] {
  const lower = capacityRange.lower
  const upper = capacityRange.upper

  return Array.from({ length: upper - lower + 1 }, (_, i) => lower + i)
}
if (import.meta.vitest) {
  const { it, describe, expect } = import.meta.vitest
  describe('reservableVisitorsCountsFromCapacity', () => {
    it('capacityRange が 1 から 3 の場合は [1, 2, 3] を返す', () => {
      expect(reservableVisitorsCountsFromCapacityRange({ lower: 1, upper: 3 })).toEqual([1, 2, 3])
    })
    it('capacityRange が 2 から 5 の場合は [2, 3, 4, 5] を返す', () => {
      expect(reservableVisitorsCountsFromCapacityRange({ lower: 2, upper: 5 })).toEqual([
        2, 3, 4, 5,
      ])
    })
  })
}

const initialVisitorsCount$ = atom((get) => {
  return initialVisitorsCount(get(reservableVisitorsCountsResult$), get(state$))
})
initialVisitorsCount$.debugLabel = `${DEBUG_PREFIX}-initialVisitorsCount$`

function initialVisitorsCount(reservableVisitorsCounts: number[], context?: CalendarContext) {
  const visitorsCount = context?.visitorsCount ?? 0
  if (visitorsCount === 0 && reservableVisitorsCounts.length > 0) {
    // 人数の初期値は、2名または予約可能人数の最小値とする
    return reservableVisitorsCounts.includes(2) ? 2 : reservableVisitorsCounts[0]
  }
  return visitorsCount
}
if (import.meta.vitest) {
  const { it, describe, expect } = import.meta.vitest
  describe('initVisitorsCount', () => {
    it('事前に人数が選択されておらず,予約可能な人数に2名がある場合は2名を選択する', () => {
      expect(initialVisitorsCount([2, 3], { visitorsCount: 0 } as CalendarContext)).toBe(2)
    })
    it('事前に人数が選択されておらず、予約可能な人数に2名がない場合は予約可能な最小の値にする', () => {
      expect(initialVisitorsCount([3, 4, 5], { visitorsCount: 0 } as CalendarContext)).toBe(3)
    })
    it('事前に人数が選択されている場合は、該当人数にする', () => {
      expect(initialVisitorsCount([1, 3, 4], { visitorsCount: 2 } as CalendarContext)).toBe(2)
    })
  })
}

const to$ = atom<DateText>((get) => {
  return getEndOfMonthAfterDays(get(now$), 3)
})
to$.debugLabel = `${DEBUG_PREFIX}-to$`

const visitorsCount$ = scope.atom<number>(0, 'visitorsCount$')

const setVisitorsCount$ = atom(null, (get, set, e: React.ChangeEvent<HTMLSelectElement>) => {
  set(visitorsCount$, elementToValue(e))
})

export function useSetVisitorsCount() {
  const set = useSetAtom(setVisitorsCount$)
  return useCallback((e: React.ChangeEvent<HTMLSelectElement>) => set(e), [set])
}

const selectedVisitorsCount$ = atom<number>((get) => {
  const visitorsCount = get(visitorsCount$)
  return visitorsCount === 0 ? get(initialVisitorsCount$) : visitorsCount
})
selectedVisitorsCount$.debugLabel = `${DEBUG_PREFIX}-selectedVisitorsCount$`

export function useVisitorsCount() {
  return useAtomValue(selectedVisitorsCount$)
}

const calendarVariables$ = atom<PlanInventoryCalendarQueryVariables>((get) => {
  const state = get(state$)
  if (state === undefined) {
    return {
      restaurantId: '' as RestaurantId,
      planId: '' as PlanId,
      to: '' as DateText,
      visitorsCount: 0,
      input: {} as PlanInventoryCalendarInput,
    }
  }
  const v = get(visitorsCount$)
  const visitorsCount = v === 0 ? get(initialVisitorsCount$) : v
  const criteria = get(criteria$)
  const to = get(to$)
  return {
    restaurantId: state.restaurantId,
    planId: state.planId,
    to,
    visitorsCount,
    input: {
      visitorsCount,
      until: to,
      seatTypes: criteria?.seatTypes,
      windowSide: criteria?.windowSide,
    },
  }
})
calendarVariables$.debugLabel = `${DEBUG_PREFIX}-calendarVariables$`

const calendarQuery$ = createQueryAtom<typeof PlanInventoryCalendar>()
calendarQuery$.debugLabel = `${DEBUG_PREFIX}-calendarQuery$`

const calendar$ = atomWithQuery((get) => {
  return createQueryOptions(
    PlanInventoryCalendar,
    get(calendarVariables$),
    get(calendarQuery$).fn,
    {
      enabled: get(reservableVisitorsCounts$).data !== undefined,
    }
  )
})
calendar$.debugLabel = `${DEBUG_PREFIX}-calendar$`

export function useCalendarQuery() {
  return useAtomValue(calendar$)
}

type CalendarItem = ResultOf<typeof InventoryCalendarItem>

const calendarResult$ = atom<readonly CalendarItem[]>(
  (get) =>
    getFragmentData(
      InventoryCalendarItem,
      get(calendar$).data?.restaurant?.plan?.inventoryCalendar
    ) ?? []
)
calendarResult$.debugLabel = `${DEBUG_PREFIX}-calendarResult$`

export function useCalendar() {
  return useAtomValue(calendarResult$)
}

const initialVisitDate$ = atom((get) => {
  return initialVisitDate(get(calendarResult$), get(state$))
})
initialVisitDate$.debugLabel = `${DEBUG_PREFIX}-initialVisitDate$`

function initialVisitDate(
  calendar: readonly ResultOf<typeof InventoryCalendarItem>[],
  context?: CalendarContext
): DateText | undefined {
  const visitDate = context?.visitDate
  if (visitDate === undefined) {
    // 日付の初期値は、直近の予約可能日とする
    const recentReservableDate = calendar.find(
      (inventoryCalendar) => inventoryCalendar.inventory.length !== 0
    )?.date.date
    return recentReservableDate
  }
  return visitDate
}
if (import.meta.vitest) {
  const { it, describe, expect } = import.meta.vitest
  describe('initVisitDate', () => {
    it('事前に日付が選択されていない場合は、直近の予約可能日を選択する', () => {
      expect(
        initialVisitDate(
          [
            {
              date: { date: '2024-01-01' as DateText } as any,
              inventory: [{ price: 1000 } as any],
            },
            {
              date: { date: '2024-01-02' as DateText } as any,
              inventory: [{ price: 2000 } as any],
            },
          ],
          {} as any
        )
      ).toBe('2024-01-01')
    })
    it('事前に日付が選択されている場合は、該当日付を選択する', () => {
      expect(initialVisitDate([], { visitDate: '2024-01-01' as DateText } as CalendarContext)).toBe(
        '2024-01-01'
      )
    })
  })
}

const visitDate$ = scope.atom<DateText | undefined>(undefined, 'visitDate$')

export function useSetVisitDate() {
  return useSetAtom(visitDate$)
}

const selectedVisitDate$ = atom<DateText | undefined>((get) => {
  return get(visitDate$) ?? get(initialVisitDate$)
})
selectedVisitDate$.debugLabel = `${DEBUG_PREFIX}-selectedVisitDate$`

export function useVisitDate() {
  return useAtomValue(selectedVisitDate$)
}

const monthlyCalendar$ = atom((get) => {
  return groupBy(get(calendarResult$), get(today$))
})
monthlyCalendar$.debugLabel = `${DEBUG_PREFIX}-monthlyCalendar$`

// 4ヶ月分のカレンダーを表示したいので当月から3ヶ月後の最終日まで取得する
function groupBy(days: readonly CalendarItem[], today: DateText): CalendarItem[][] {
  const currentMonth = getMonth(today)
  // days が sort されている前提
  return days.reduce<CalendarItem[][]>((result, day) => {
    const targetMonth = getMonth(day.date.date)
    const diff = targetMonth - currentMonth
    // 同じ年なら素直に計算できる (202407 で来月の場合は 8 - 7 = 1)
    // 今日が 2024-11-13 で 2025-02-01 の場合は
    // 2 - 11 + 12 = 3 で 202411, 202412, 202501, 202502 と4つ目の index になる
    const monthIndex = diff >= 0 ? diff : diff + 12
    ;(result[monthIndex] ??= []).push(day)
    return result
  }, [] satisfies CalendarItem[][])
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('groupBy', () => {
    expect(groupBy(input as unknown as readonly CalendarItem[], '2024-07-08' as DateText)).toEqual(
      output
    )
  })
}

export function useMonthlyCalendar() {
  return useAtomValue(monthlyCalendar$)
}

const criteria$ = scope.atom<
  Pick<AdditionalCriteria, 'seatTypes' | 'windowSide' | 'smokingAllowed' | 'childUses'> | undefined
>(undefined, 'criteria$')

const setCriteria$ = atom(null, (_, set, context?: CalendarContext) => {
  set(criteria$, {
    seatTypes: context?.seatTypes,
    windowSide: context?.windowSide,
    smokingAllowed: context?.smokingAllowed,
    childUses: context?.childUses,
  })
})
setCriteria$.debugLabel = `${DEBUG_PREFIX}-setCriteria$`

export function useSetCriteria() {
  return useSetAtom(setCriteria$)
}

export function useCriteria() {
  return useAtomValue(criteria$)
}

const filterLabel$ = atom((get) => {
  return createFilterLabel(get(criteria$))
})
filterLabel$.debugLabel = `${DEBUG_PREFIX}-filterLabel$`

export function useFilterLabel() {
  return useAtomValue(filterLabel$)
}

function createFilterLabel(criteria?: AdditionalCriteria) {
  if (criteria === undefined) {
    return ''
  }
  const labels = []
  if (criteria.windowSide) {
    labels.push('窓側席')
  }
  if (criteria.seatTypes?.includes('COUNTER')) {
    labels.push('カウンター席')
  }
  if (criteria.seatTypes?.includes('TERRACE')) {
    labels.push('テラス席')
  }
  if (
    criteria.seatTypes?.includes('PRIVATE_ROOM') &&
    criteria.seatTypes?.includes('SEMI_PRIVATE_ROOM')
  ) {
    labels.push('個室（半個室を含む）')
  } else if (criteria.seatTypes?.includes('PRIVATE_ROOM')) {
    labels.push('個室')
  }
  if (criteria.seatTypes?.includes('TABLE')) {
    labels.push('テーブル席')
  }
  if (criteria.smokingAllowed !== undefined) {
    labels.push(criteria.smokingAllowed ? '喫煙' : '禁煙')
  }
  if (criteria.childUses) {
    // 広い年齢層を優先する
    // 中学生以上はFilterChipで表示しないのでラベルを追加しない
    if (criteria.childUses.includes('PRE_SCHOOLERS')) {
      labels.push('乳幼児可')
    } else if (criteria.childUses.includes('GRADE_SCHOOLERS')) {
      labels.push('小学生から可')
    }
  }
  return labels.join('、')
}
if (import.meta.vitest) {
  const { describe, test, expect } = import.meta.vitest
  describe('createFilterLabel', () => {
    test('空条件', () => {
      expect(createFilterLabel({} as CalendarContext)).toBe('')
    })

    test('条件いろいろ', () => {
      expect(
        createFilterLabel({
          seatTypes: ['COUNTER', 'PRIVATE_ROOM', 'SEMI_PRIVATE_ROOM', 'TERRACE', 'TABLE'],
          windowSide: true,
          smokingAllowed: false,
          childUses: ['GRADE_SCHOOLERS'],
        } as CalendarContext)
      ).toBe('窓側席、カウンター席、テラス席、個室（半個室を含む）、テーブル席、禁煙、小学生から可')
    })

    test('半個室なし', () => {
      expect(
        createFilterLabel({
          seatTypes: ['COUNTER', 'PRIVATE_ROOM', 'TERRACE', 'TABLE'],
          windowSide: true,
          smokingAllowed: false,
          childUses: ['GRADE_SCHOOLERS'],
        } as CalendarContext)
      ).toBe('窓側席、カウンター席、テラス席、個室、テーブル席、禁煙、小学生から可')
    })

    test('喫煙のみ', () => {
      expect(
        createFilterLabel({
          smokingAllowed: true,
        } as CalendarContext)
      ).toBe('喫煙')
    })

    test('乳幼児可', () => {
      expect(
        createFilterLabel({
          childUses: ['PRE_SCHOOLERS', 'GRADE_SCHOOLERS'],
        } as CalendarContext)
      ).toBe('乳幼児可')
    })

    test('中学生以上は表示しない', () => {
      expect(
        createFilterLabel({
          childUses: ['OVER_MIDDLE_SCHOOLERS'],
        } as CalendarContext)
      ).toBe('')
    })
  })
}
